import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { apiErrorMessage } from './utils/apiErrors';
import { bankService } from './clients/ApiClients';
import { PayableReturn } from './clients/rust-api-client';
import { formatCurrency } from './utils/bankHelpers';

interface PayableCancelDialogProps {
  /** The item to cancel; `null` closes the dialog. */
  item: PayableReturn | null;
  onClose: () => void;
  onCancelled: (after: PayableReturn) => void;
}

/**
 * Cancel one payable at the bank. Works while the bank still holds it —
 * pending, or settled and not yet processing. A settled item's ledger
 * posting is reversed. The bank's answer decides: anything other than
 * `Cancelled` leaves the row as it was.
 */
const PayableCancelDialog: React.FC<PayableCancelDialogProps> = ({
  item,
  onClose,
  onCancelled,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) setError(null);
  }, [item]);

  const handleSubmit = async () => {
    if (!item) return;
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.cancelHoaPayable({
        hoaId: item.hoa_id,
        id: item.id,
      });
      onCancelled(resp.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't cancel."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!item} onClose={submitting ? () => undefined : onClose}>
      <DialogTitle>Cancel this {item?.direction ?? 'payable'}?</DialogTitle>
      <DialogDescription>
        {item ? formatCurrency(Number(item.amount)) : ''}
        {item?.memo ? ` · ${item.memo}` : ''}. The bank drops it
        {item?.status === 'settled' ? ' and the ledger entry is reversed' : ''}.
        To pay it later, submit it again.
      </DialogDescription>
      <DialogBody>
        {error && (
          <ErrorAlert
            className="mb-3"
            title="Not cancelled"
            onDismiss={() => setError(null)}
          >
            {error}
          </ErrorAlert>
        )}
        {item?.status === 'settled' && (
          <Text className="text-sm">
            This item was settled. The bank will refuse once it has started
            processing; if so, the row stays as it is.
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={submitting}>
          Keep it
        </Button>
        <Button color="red" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Cancelling…' : 'Cancel at the bank'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayableCancelDialog;
